import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, Eye, EyeOff, KeyRound, Lock, X } from "lucide-react";
import { API_BASE_URL } from "../config";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPasswordForm({
  uid: uidProp,
  token: tokenProp,
  embedded = false,
  onClose,
  onSuccess,
  onRequestResetLink,
} = {}) {
  const q = useQuery();
  const navigate = useNavigate();
  const uid = uidProp ?? q.get("uid") ?? "";
  const token = tokenProp ?? q.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [feedback, setFeedback] = useState(null); // { type: "success" | "error", text: string }
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fieldWrapperClasses =
    "flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-sm text-slate-900 shadow-inner shadow-white/40 transition focus-within:border-slate-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-slate-400";
  const inputClasses =
    "flex-1 border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0";
  const buttonClasses =
    "inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 transition-colors duration-200 transition-transform hover:-translate-y-1 hover:scale-105 hover:from-slate-800 hover:to-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60";

  const feedbackClasses = feedback
    ? `rounded-2xl border px-4 py-3 text-sm text-center ${
        feedback.type === "error"
          ? "border-rose-300 bg-rose-50 text-rose-600"
          : "border-emerald-300 bg-emerald-50 text-emerald-700"
      }`
    : "";

  const handleClose = () => {
    if (embedded) {
      onClose?.();
    } else {
      navigate("/");
    }
  };

  const handleRequestResetLink = () => {
    if (embedded) {
      onRequestResetLink?.();
    } else {
      navigate("/forgot-password");
    }
  };

  async function onSubmit(event) {
    event.preventDefault();
    if (password.length < 8) {
      setFeedback({ type: "error", text: "Password must be at least 8 characters long." });
      return;
    }
    if (!uid || !token) {
      setFeedback({ type: "error", text: "The reset link is invalid or expired." });
      return;
    }
    setLoading(true);
    setFeedback(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/password-reset/confirm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ uid, token, password, password2: password }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Failed to change the password.");
      setFeedback({ type: "success", text: "Password updated - redirecting to sign in..." });
      setTimeout(() => {
        if (embedded) {
          onSuccess?.();
        } else {
          navigate("/login");
        }
      }, 1500);
    } catch (error) {
      setFeedback({ type: "error", text: error.message || "We could not change the password. Try again." });
    } finally {
      setLoading(false);
    }
  }

  const cardClasses = embedded
    ? "relative mx-auto w-full max-w-md rounded-2xl border border-white/20 bg-white/95 p-6 text-slate-900 shadow-[0_35px_120px_-45px_rgba(15,23,42,0.8)] backdrop-blur-2xl sm:p-8"
    : "relative rounded-3xl border border-white/15 bg-white/95 p-8 text-slate-900 shadow-[0_35px_120px_-45px_rgba(15,23,42,0.8)] backdrop-blur-2xl sm:p-10";

  const closeButton = (
    <button
      type="button"
      onClick={handleClose}
      className="absolute right-6 top-6 z-30 flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900/95 text-white shadow-lg shadow-slate-900/30 transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-white"
      aria-label="Close"
    >
      <X size={18} />
    </button>
  );

  const invalidLinkContent = (
    <div className="space-y-6 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500 text-white shadow-lg shadow-rose-900/30">
        <AlertCircle size={30} />
      </div>
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-slate-900">Reset link unavailable</h1>
        <p className="text-sm text-slate-500">
          The password reset link is invalid or has expired. Request a new one to continue.
        </p>
      </div>
      <button
        type="button"
        onClick={handleRequestResetLink}
        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 transition hover:-translate-y-1 hover:scale-105 hover:from-slate-800 hover:to-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white"
      >
        <KeyRound size={18} />
        Request reset link
      </button>
    </div>
  );

  const formContent = (
    <div className="space-y-6">
      <div className="space-y-3 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
          <KeyRound size={28} />
        </div>
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold text-slate-900">Set a new password</h1>
          <p className="text-sm text-slate-500">Choose a strong password you have not used before.</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <fieldset disabled={loading} className="space-y-4">
          <label className="text-sm font-medium text-slate-600">
            <span className="sr-only">New password</span>
            <div className={`${fieldWrapperClasses} pr-2`}>
              <Lock size={20} className="text-slate-400" />
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="New password (min. 8 characters)"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={inputClasses}
                minLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="inline-flex items-center justify-center text-slate-500 transition hover:text-slate-700 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </label>
        </fieldset>
        <button className={buttonClasses} disabled={loading}>
          {loading ? (
            "Saving..."
          ) : (
            <>
              <CheckCircle2 size={18} />
              Set new password
            </>
          )}
        </button>
        {feedback ? (
          <div className={feedbackClasses} role={feedback.type === "error" ? "alert" : "status"} aria-live="polite">
            {feedback.text}
          </div>
        ) : null}
      </form>
    </div>
  );

  const card = (
    <div className={cardClasses}>
      {closeButton}
      {uid && token ? formContent : invalidLinkContent}
    </div>
  );

  if (embedded) {
    return card;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="absolute inset-0">
        <div className="pointer-events-none absolute -top-40 left-24 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
        <div className="pointer-events-none absolute bottom-16 right-10 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />
      </div>
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-md" />
      <div className="relative flex min-h-screen items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-md">{card}</div>
      </div>
    </div>
  );
}
